import { HackerAPIFile } from "src/shared/utils/hackerapi";

/**
 * Reuse a signed download URI by stable file id until near expiry so
 * <img> HTTP cache can hit across Directory refreshes and searches.
 */

export const SIGNED_FILE_URL_CACHE_STORAGE_KEY = "htn:signed-file-urls:v1";
export const SIGNED_FILE_URL_EXPIRY_SKEW_MS = 60_000;
export const SIGNED_FILE_URL_CACHE_MAX_ENTRIES = 500;

type SignedFileUrlEntry = {
  uri: string;
  expiry: number;
};

type SignedFileUrlCacheStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

const memoryCache = new Map<number, SignedFileUrlEntry>();
let hydratedFromSession = false;

const getSessionStorage = (): SignedFileUrlCacheStorage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * HackerAPI returns a signed file's `expiry` as a stringified epoch-ms
 * timestamp (`File.getFileDownload`). Exported so other signed-file consumers
 * parse it the same way.
 */
export const parseExpiryMs = (expiry: unknown): number | null => {
  if (expiry == null) return null;
  const value =
    typeof expiry === "number" ? expiry : Number.parseInt(String(expiry), 10);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const isUsable = (entry: SignedFileUrlEntry, now: number): boolean =>
  entry.uri.length > 0 && entry.expiry - SIGNED_FILE_URL_EXPIRY_SKEW_MS > now;

const hydrateFromSession = (
  storage: SignedFileUrlCacheStorage | null = getSessionStorage(),
  now = Date.now()
) => {
  if (hydratedFromSession || !storage) {
    hydratedFromSession = true;
    return;
  }
  hydratedFromSession = true;

  try {
    const raw = storage.getItem(SIGNED_FILE_URL_CACHE_STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return;

    Object.entries(parsed as Record<string, unknown>).forEach(
      ([key, value]) => {
        const fileId = Number(key);
        if (
          !Number.isInteger(fileId) ||
          fileId <= 0 ||
          !value ||
          typeof value !== "object"
        ) {
          return;
        }
        const entry = value as Partial<SignedFileUrlEntry>;
        if (
          typeof entry.uri !== "string" ||
          typeof entry.expiry !== "number" ||
          !isUsable({ uri: entry.uri, expiry: entry.expiry }, now)
        ) {
          return;
        }
        memoryCache.set(fileId, { uri: entry.uri, expiry: entry.expiry });
      }
    );
  } catch {
    try {
      storage.removeItem(SIGNED_FILE_URL_CACHE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

const persistToSession = (
  storage: SignedFileUrlCacheStorage | null = getSessionStorage()
) => {
  if (!storage) return;
  const serialized: Record<string, SignedFileUrlEntry> = {};
  memoryCache.forEach((entry, fileId) => {
    serialized[String(fileId)] = entry;
  });
  try {
    storage.setItem(
      SIGNED_FILE_URL_CACHE_STORAGE_KEY,
      JSON.stringify(serialized)
    );
  } catch {
    // Quota / private mode — memory cache still works for the tab.
  }
};

const evictOverflow = () => {
  if (memoryCache.size <= SIGNED_FILE_URL_CACHE_MAX_ENTRIES) return;
  const overflow = memoryCache.size - SIGNED_FILE_URL_CACHE_MAX_ENTRIES;
  const keys = memoryCache.keys();
  for (let i = 0; i < overflow; i += 1) {
    const next = keys.next();
    if (next.done) break;
    memoryCache.delete(next.value);
  }
};

export const resetSignedFileUrlCacheForTests = () => {
  memoryCache.clear();
  hydratedFromSession = false;
};

export const invalidateSignedFileUrl = (
  fileId: number,
  storage: SignedFileUrlCacheStorage | null = getSessionStorage(),
  now = Date.now()
) => {
  if (!Number.isInteger(fileId) || fileId <= 0) return;
  hydrateFromSession(storage, now);
  memoryCache.delete(fileId);
  persistToSession(storage);
};

/**
 * Returns a stable signed URI for this file id when a fresh cached entry exists.
 */
export const resolveStableSignedFileUri = (
  file: Pick<HackerAPIFile, "id" | "uri" | "expiry">,
  now = Date.now(),
  storage: SignedFileUrlCacheStorage | null = getSessionStorage()
): string => {
  hydrateFromSession(storage, now);

  const fileId = file.id;
  const incomingUri = file.uri;
  if (!Number.isInteger(fileId) || fileId <= 0 || !incomingUri) {
    return incomingUri;
  }

  const cached = memoryCache.get(fileId);
  if (cached && isUsable(cached, now)) {
    return cached.uri;
  }

  const expiry = parseExpiryMs(file.expiry);
  if (expiry == null || !isUsable({ uri: incomingUri, expiry }, now)) {
    // Expired or unknown expiry: do not stick a dead URL in the cache.
    memoryCache.delete(fileId);
    persistToSession(storage);
    return incomingUri;
  }

  memoryCache.set(fileId, { uri: incomingUri, expiry });
  evictOverflow();
  persistToSession(storage);
  return incomingUri;
};

export const withStableSignedFileUri = (
  file: HackerAPIFile,
  now = Date.now(),
  storage: SignedFileUrlCacheStorage | null = getSessionStorage()
): HackerAPIFile => {
  const uri = resolveStableSignedFileUri(file, now, storage);
  if (uri === file.uri) return file;
  return { ...file, uri };
};
